import { apiClient } from './apiClient';
import {
  CPE,
  CPEFormData,
  CPEFilters,
  CPEListResponse,
  CPEResponse,
  CPEAuditLogResponse,
  ValidateLocationResponse,
} from '../types/cpe';

class CPEService {
  private api = apiClient;

  /**
   * Get list of CPEs with pagination and filters
   */
  async getCPEs(
    page: number = 1,
    limit: number = 20,
    filters?: CPEFilters
  ): Promise<CPEListResponse> {
    const params: Record<string, unknown> = { page, limit };

    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.plant_type && filters.plant_type.length > 0) {
        params.plant_type = filters.plant_type;
      }
      if (filters.cpe_status && filters.cpe_status.length > 0) {
        params.cpe_status = filters.cpe_status;
      }
      if (filters.commercial_status && filters.commercial_status.length > 0) {
        params.commercial_status = filters.commercial_status;
      }
      if (filters.internal_positioning && filters.internal_positioning.length > 0) {
        params.internal_positioning = filters.internal_positioning;
      }
      if (filters.energization_year) params.energization_year = filters.energization_year;
      if (filters.min_power !== undefined) params.min_power = filters.min_power;
      if (filters.max_power !== undefined) params.max_power = filters.max_power;
      if (filters.om_account_manager) params.om_account_manager = filters.om_account_manager;
      if (filters.zone_id) params.zone_id = filters.zone_id;
      if (filters.include_deleted) params.include_deleted = 'true';
      if (filters.is_waldevar_pp !== undefined) params.is_waldevar_pp = filters.is_waldevar_pp;
      if (filters.updated_from) params.updated_from = filters.updated_from;
      if (filters.updated_to) params.updated_to = filters.updated_to;
      if (filters.next_action_id) params.next_action_id = filters.next_action_id;
      if (filters.overdue_next_action) params.overdue_next_action = 'true';
    }

    const response = await this.api.get<CPEListResponse>('/cpes', { params });
    return response.data;
  }

  /**
   * Get single CPE by ID
   */
  async getCPEById(id: string): Promise<CPE> {
    const response = await this.api.get<CPEResponse>(`/cpes/${id}`);
    return response.data.data.cpe;
  }

  /**
   * Create new CPE
   */
  async createCPE(data: CPEFormData): Promise<CPE> {
    const response = await this.api.post<CPEResponse>('/cpes', data);
    return response.data.data.cpe;
  }

  /**
   * Update existing CPE
   */
  async updateCPE(id: string, data: Partial<CPEFormData>, currentUpdatedAt?: string): Promise<CPE> {
    const payload: Partial<CPEFormData> & { current_updated_at?: string } = { ...data };
    if (currentUpdatedAt) {
      payload.current_updated_at = currentUpdatedAt;
    }
    const response = await this.api.put<CPEResponse>(`/cpes/${id}`, payload);
    return response.data.data.cpe;
  }

  /**
   * Soft-delete CPE
   */
  async deleteCPE(id: string): Promise<void> {
    await this.api.delete(`/cpes/${id}`);
  }

  /**
   * Restore soft-deleted CPE (Super Admin only)
   */
  async restoreCPE(id: string): Promise<CPE> {
    const response = await this.api.post<CPEResponse>(`/cpes/${id}/restore`);
    return response.data.data.cpe;
  }

  /**
   * Get audit log for a CPE
   */
  async getCPEAuditLog(id: string): Promise<CPEAuditLogResponse> {
    const response = await this.api.get<CPEAuditLogResponse>(`/cpes/${id}/audit-log`);
    return response.data;
  }

  /**
   * Upload CPE image
   */
  async uploadCPEImage(id: string, file: File): Promise<CPE> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await this.api.post<CPEResponse>(
      `/cpes/${id}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data.cpe;
  }

  /**
   * Delete CPE image
   */
  async deleteCPEImage(id: string): Promise<void> {
    await this.api.delete(`/cpes/${id}/image`);
  }

  /**
   * Validate location (check if coordinates exist)
   */
  async validateLocation(
    latitude: number,
    longitude: number,
    excludeId?: string
  ): Promise<ValidateLocationResponse> {
    const response = await this.api.post<ValidateLocationResponse>(
      '/cpes/validate-location',
      { latitude, longitude, excludeId }
    );
    return response.data;
  }

  /**
   * Generate CPE report (CSV)
   */
  async generateReport(
    startDate?: string,
    endDate?: string,
    filters?: CPEFilters
  ): Promise<Blob> {
    const params: Record<string, unknown> = {};

    // Use filters' updated_from/updated_to if available, otherwise fall back to startDate/endDate
    if (filters?.updated_from || startDate) {
      params.updated_from = filters?.updated_from || startDate;
    }
    if (filters?.updated_to || endDate) {
      params.updated_to = filters?.updated_to || endDate;
    }

    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.plant_type && filters.plant_type.length > 0) {
        params.plant_type = filters.plant_type;
      }
      if (filters.cpe_status && filters.cpe_status.length > 0) {
        params.cpe_status = filters.cpe_status;
      }
      if (filters.commercial_status && filters.commercial_status.length > 0) {
        params.commercial_status = filters.commercial_status;
      }
      if (filters.internal_positioning && filters.internal_positioning.length > 0) {
        params.internal_positioning = filters.internal_positioning;
      }
      if (filters.energization_year) params.energization_year = filters.energization_year;
      if (filters.min_power !== undefined) params.min_power = filters.min_power;
      if (filters.max_power !== undefined) params.max_power = filters.max_power;
      if (filters.om_account_manager) params.om_account_manager = filters.om_account_manager;
      if (filters.is_waldevar_pp !== undefined) params.is_waldevar_pp = filters.is_waldevar_pp;
      if (filters.next_action_id) params.next_action_id = filters.next_action_id;
      if (filters.overdue_next_action) params.overdue_next_action = 'true';
    }

    const response = await this.api.post('/reports/cpes', {}, {
      params,
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Get image URL
   */
  getImageUrl(imageUrl: string | null): string | null {
    if (!imageUrl) return null;
    // If it's already a full URL, return it
    if (imageUrl.startsWith('http')) return imageUrl;
    // Otherwise, return as-is (it will be relative to the current domain)
    // e.g., /uploads/image.jpg will be served by nginx proxy
    return imageUrl;
  }
}

export const cpeService = new CPEService();
